import React, { useState, useEffect, useCallback } from 'react';
import AnimatedModal from '../shared/AnimatedModal';
import { Snackbar } from '@material-ui/core';

const STATUS_OPTIONS = ['', 'claimed', 'used', 'expired'];
const STATUS_BADGES = { claimed: 'badge-warning', used: 'badge-success', expired: 'badge-secondary' };
const STATUS_LABELS = { claimed: 'Claimed', used: 'Used', expired: 'Expired' };

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
};

const buildPageNumbers = (current, total) => {
  const delta = 2;
  const pages = [];
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  pages.push(1);
  if (left > 2) pages.push('...');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('...');
  if (total > 1) pages.push(total);

  return pages;
};

const DetailRow = ({ icon, label, children }) => (
  <div className="d-flex align-items-start py-2 border-bottom">
    <div className="text-muted d-flex align-items-center" style={{ width: '140px', flexShrink: 0 }}>
      <i className={`zmdi ${icon} mr-2`} />
      <small>{label}</small>
    </div>
    <div className="flex-fill font-weight-bold">{children}</div>
  </div>
);

const UserVouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);

  // Modal states
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [viewModal, setViewModal] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.append('q[voucher_code_or_account_full_name_cont]', search);
    if (statusFilter) params.append('status', statusFilter);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('page', page);
    params.append('per_page', 20);

    try {
      const response = await fetch(`/api/admin/loyalty/vouchers?${params}`);
      const json = await response.json();
      if (json.success) {
        setVouchers(json.data);
        setPagination(json.pagination || {});
      }
    } catch (e) {
      console.error('Failed to fetch vouchers', e);
    }
    setLoading(false);
  }, [search, statusFilter, startDate, endDate, page]);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  const handleRedeem = async (code) => {
    setProcessing(true);
    try {
      const response = await fetch('/api/admin/loyalty/vouchers/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ voucher_code: code })
      });
      const json = await response.json();
      if (json.success) {
        setRedeemModalOpen(false);
        setConfirmModal(null);
        setRedeemCode('');
        fetchVouchers();
        setSnackbar({ open: true, message: 'Voucher redeemed successfully' });
      } else {
        setSnackbar({ open: true, message: json.errors?.[0] || 'Failed to redeem voucher' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Network error. Please try again.' });
    }
    setProcessing(false);
  };

  const redeemModalFooter = (handleClose) => (
    <>
      <button className="btn btn-outline-secondary" onClick={handleClose}>Cancel</button>
      <button
        className="btn btn-success"
        disabled={!redeemCode.trim() || processing}
        onClick={() => handleRedeem(redeemCode.trim())}
      >
        {processing ? 'Processing...' : 'Process'}
      </button>
    </>
  );

  const confirmModalFooter = (handleClose) => (
    <>
      <button className="btn btn-outline-secondary" onClick={handleClose}>Cancel</button>
      <button
        className="btn btn-success"
        disabled={processing}
        onClick={() => handleRedeem(confirmModal.voucher_code)}
      >
        {processing ? 'Processing...' : 'Confirm Redeem'}
      </button>
    </>
  );

  return (
    <div className="p-4">
      {/* Filters */}
      <div className="d-flex flex-wrap align-items-end mb-4">
        <div className="form-group mb-0 flex-fill mr-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search user name, member ID, or voucher code..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <i className="form-group__bar" />
        </div>
        <div className="form-group mb-0 mr-3">
          <label className="small text-muted mb-1">Status</label>
          <select className="form-control" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            {STATUS_OPTIONS.filter(Boolean).map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
        <div className="form-group mb-0 mr-3">
          <label className="small text-muted mb-1">Start Date</label>
          <input type="date" className="form-control" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
        </div>
        <div className="form-group mb-0 mr-3">
          <label className="small text-muted mb-1">End Date</label>
          <input type="date" className="form-control" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
        </div>
        <button className="btn btn-success mb-0" onClick={() => setRedeemModalOpen(true)}>
          <i className="zmdi zmdi-tag mr-1" /> Manual Redeem
        </button>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>User Information</th>
              <th>Reward Name</th>
              <th>Voucher Code</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
            ) : vouchers.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-4 text-muted">No vouchers found</td></tr>
            ) : vouchers.map(v => (
              <tr key={v.id}>
                <td className="text-nowrap">{formatDate(v.claimed_at)}</td>
                <td>
                  <div className="font-weight-bold">{v.account_name || '-'}</div>
                  <small className="text-muted">{v.member_id || '-'}</small>
                </td>
                <td>{v.reward_name}</td>
                <td><code>{v.voucher_code}</code></td>
                <td>
                  <span className={`badge ${STATUS_BADGES[v.status] || 'badge-secondary'}`}>
                    {STATUS_LABELS[v.status] || v.status}
                  </span>
                </td>
                <td>
                  {v.status === 'claimed' && (
                    <button
                      className="btn btn-sm btn-success mr-1"
                      onClick={() => setConfirmModal({ voucher_code: v.voucher_code, participant_name: v.account_name })}
                    >
                      <i className="zmdi zmdi-check mr-1" /> Redeem
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    title="View Details"
                    onClick={() => setViewModal(v)}
                  >
                    <i className="zmdi zmdi-eye" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_count} total)
          </small>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <a className="page-link" href="#" onClick={(e) => { e.preventDefault(); if (page > 1) setPage(page - 1); }}>
                  <i className="zmdi zmdi-chevron-left" />
                </a>
              </li>
              {buildPageNumbers(page, pagination.total_pages).map((p, idx) => (
                p === '...' ? (
                  <li key={`ellipsis-${idx}`} className="page-item disabled">
                    <span className="page-link">...</span>
                  </li>
                ) : (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <a className="page-link" href="#" onClick={(e) => { e.preventDefault(); setPage(p); }}>{p}</a>
                  </li>
                )
              ))}
              <li className={`page-item ${page === pagination.total_pages ? 'disabled' : ''}`}>
                <a className="page-link" href="#" onClick={(e) => { e.preventDefault(); if (page < pagination.total_pages) setPage(page + 1); }}>
                  <i className="zmdi zmdi-chevron-right" />
                </a>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* View Detail Modal */}
      {viewModal && (
        <AnimatedModal
          title="Voucher Details"
          onClose={() => setViewModal(null)}
        >
          {/* Voucher header */}
          <div className="text-center pb-3 mb-3 border-bottom">
            <div className="mb-2">
              <span className={`badge ${STATUS_BADGES[viewModal.status] || 'badge-secondary'} px-3 py-1`}>
                {STATUS_LABELS[viewModal.status] || viewModal.status}
              </span>
            </div>
            <h5 className="mb-1">{viewModal.reward_name}</h5>
            <code className="text-muted">{viewModal.voucher_code}</code>
          </div>

          {/* User information */}
          <h6 className="text-muted text-uppercase small mb-2">
            <i className="zmdi zmdi-account mr-1" /> User Information
          </h6>
          <div className="card card-body p-3 mb-3">
            <DetailRow icon="zmdi-account" label="Name">{viewModal.account_name || '-'}</DetailRow>
            <DetailRow icon="zmdi-card" label="Member ID">{viewModal.member_id || '-'}</DetailRow>
            {viewModal.participant_name && (
              <DetailRow icon="zmdi-run" label="Participant">{viewModal.participant_name}</DetailRow>
            )}
            {viewModal.participant_bib && (
              <DetailRow icon="zmdi-bookmark" label="BIB Number">{viewModal.participant_bib}</DetailRow>
            )}
          </div>

          {/* Timeline */}
          <h6 className="text-muted text-uppercase small mb-2">
            <i className="zmdi zmdi-time mr-1" /> Timeline
          </h6>
          <div className="card card-body p-3 mb-0">
            <DetailRow icon="zmdi-assignment-check" label="Claimed">{formatDate(viewModal.claimed_at)}</DetailRow>
            <DetailRow icon="zmdi-check-circle" label="Redeemed">{formatDate(viewModal.redeemed_at)}</DetailRow>
            <div className="d-flex align-items-start py-2">
              <div className="text-muted d-flex align-items-center" style={{ width: '140px', flexShrink: 0 }}>
                <i className="zmdi zmdi-time-countdown mr-2" />
                <small>Expires</small>
              </div>
              <div className="flex-fill font-weight-bold">{formatDate(viewModal.expires_at)}</div>
            </div>
          </div>
        </AnimatedModal>
      )}

      {/* Manual Redeem Modal */}
      {redeemModalOpen && (
        <AnimatedModal
          title="Manual Redeem"
          onClose={() => { setRedeemModalOpen(false); setRedeemCode(''); }}
          footer={redeemModalFooter}
        >
          <p className="text-muted">Enter the voucher code to process redemption manually.</p>
          <div className="form-group">
            <label>Enter Voucher Code *</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., HR-12345"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              autoFocus
            />
            <i className="form-group__bar" />
          </div>
        </AnimatedModal>
      )}

      {/* Confirm Redeem Modal */}
      {confirmModal && (
        <AnimatedModal
          title="Confirm Redemption"
          onClose={() => setConfirmModal(null)}
          footer={confirmModalFooter}
        >
          <p>
            Are you sure you want to redeem voucher code{' '}
            <code>{confirmModal.voucher_code}</code>{' '}
            for <strong>{confirmModal.participant_name || 'this user'}</strong>?
          </p>
          <div className="alert alert-warning">
            <i className="zmdi zmdi-alert-triangle mr-1" /> This action cannot be undone.
          </div>
        </AnimatedModal>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  );
};

export default UserVouchers;
